import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
} from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';

const Login = () => {
  const [searchParams] = useSearchParams();

  const azureError = searchParams.get('error');
  const azureErrorMsg = azureError
    ? {
        account_not_exist: 'No account exists for this Microsoft email.',
        account_inactive: 'Your account is deactivated. Contact admin.',
        login_failed: 'Microsoft sign-in failed. Please try again.',
        azure_unavailable: 'Microsoft sign-in is currently unavailable.',
        no_email: 'No email returned from Microsoft.',
        domain_not_allowed:
          'Only Tech4logic Microsoft accounts are allowed to sign in.',
        session_expired:
          'Your sign-in session expired. Please click "Sign in with Microsoft" again.',
      }[azureError] || 'Sign-in failed.'
    : '';

  const handleMicrosoftSignIn = () => {
    const apiBase = process.env.REACT_APP_API_URL || '/api';
    window.location.href = `${apiBase}/auth/azure`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #875A7B 0%, #714B68 50%, #9a6b8c 100%)',
        position: 'relative',
        overflow: 'hidden',
        p: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 60%)',
          animation: 'pulse 15s ease-in-out infinite',
        },
        '@keyframes pulse': {
          '0%, 100%': { transform: 'scale(1) rotate(0deg)' },
          '50%': { transform: 'scale(1.1) rotate(180deg)' },
        },
      }}
    >
      {/* Floating shapes */}
      <Box
        sx={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
          top: '-100px',
          right: '-100px',
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-20px)' },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)',
          bottom: '-50px',
          left: '-50px',
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />

      <Card
        sx={{
          maxWidth: 440,
          width: '100%',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          position: 'relative',
          zIndex: 1,
          overflow: 'visible',
          '&:hover': {
            transform: 'none',
          },
        }}
      >
        <CardContent sx={{ p: 5 }}>
          {/* Logo Section */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 70,
                height: 70,
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #875A7B 0%, #714B68 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                boxShadow: '0 10px 30px rgba(135, 90, 123, 0.4)',
              }}
            >
              <AutoAwesome sx={{ color: 'white', fontSize: 35 }} />
            </Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(135deg, #875A7B 0%, #714B68 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
              }}
            >
              T4L CRM
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Quotation Builder Platform
            </Typography>
          </Box>

          <Typography variant="h5" component="h2" sx={{ mb: 1, fontWeight: 600 }}>
            Welcome back
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sign in with your Tech4logic Microsoft account to continue.
          </Typography>

          {azureErrorMsg && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {azureErrorMsg}
            </Alert>
          )}

          {/* Microsoft SSO — official Microsoft-branded button */}
          <Box
            component="button"
            type="button"
            onClick={handleMicrosoftSignIn}
            aria-label="Sign in with Microsoft"
            sx={{
              // Microsoft brand spec: 41px height, white bg, #8C8C8C border,
              // Segoe UI 15px, #5E5E5E text, logo + 12px gap + label.
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              width: '100%',
              height: 41,
              px: '12px',
              cursor: 'pointer',
              background: '#FFFFFF',
              border: '1px solid #8C8C8C',
              borderRadius: 0,
              fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              fontSize: 15,
              fontWeight: 600,
              color: '#5E5E5E',
              letterSpacing: 0,
              transition: 'background-color 120ms ease, border-color 120ms ease',
              '& svg': {
                width: 21,
                height: 21,
                flex: '0 0 21px',
                display: 'block',
              },
              '&:hover': {
                background: '#F5F5F5',
                borderColor: '#5E5E5E',
              },
              '&:focus-visible': {
                outline: '2px solid #2F2F2F',
                outlineOffset: 2,
              },
              '&:active': {
                background: '#EBEBEB',
              },
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="21"
              height="21"
              viewBox="0 0 21 21"
              aria-hidden="true"
              focusable="false"
            >
              <rect x="0" y="0" width="10" height="10" fill="#F25022" />
              <rect x="11" y="0" width="10" height="10" fill="#7FBA00" />
              <rect x="0" y="11" width="10" height="10" fill="#00A4EF" />
              <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
            </svg>
            <span>Sign in with Microsoft</span>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 2 }}
          >
            Use your <strong>@tech4logic.com</strong> Microsoft account.
            <br />
            New users are created automatically on first sign-in.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
